import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';

export interface LanguageSettings {
  loader: boolean;
  data: Record<string, string> | null;
  state: 'waiting' | 'completed' | 'error';
  region: string | null;
  en: Record<string, string> | null;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  _languageStateSubject: BehaviorSubject<LanguageSettings> = new BehaviorSubject<LanguageSettings>({
    loader: false,
    data: null,
    state: 'waiting',
    region: null,
    en: null,
  });

  currentLanguage!: string;

  private _currentLanguage$ = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient) {}

  public get currentLanguage$(): Observable<string | null> {
    return this._currentLanguage$.asObservable();
  }

  public setLanguageState(languageSettings: LanguageSettings) {
    localStorage.setItem('translationList', JSON.stringify(languageSettings));
    this._languageStateSubject.next(languageSettings);
  }

  setLanguage(region: string) {
    this.currentLanguage = region;
    this._currentLanguage$.next(region);

    forkJoin({
      local: this.getLocalDictionaries(region),
      remote: this.getRemoteDictionaries(region),
    }).subscribe((results) => {
      const languageDictionary = this.mergeDictionaries(results.local[0], results.local[1], results.remote[0]);
      this.setLanguageState({
        loader: false,
        data: languageDictionary ? languageDictionary : null,
        state: results.local[1] ? 'completed' : 'error',
        region: region,
        en: results.local[0],
      });
    });
  }

  private mergeDictionaries(
    english: Record<string, string>,
    local: Record<string, string>,
    remote: Record<string, string>
  ): Record<string, string> {
    let mergedDictionary = { ...english };
    if (local) {
      mergedDictionary = {
        ...mergedDictionary,
        ...local,
      };
    }
    if (remote) {
      mergedDictionary = {
        ...mergedDictionary,
        ...remote,
      };
    }
    return mergedDictionary;
  }

  private getLocalDictionaries(region: string): Observable<Record<string, string>[]> {
    const obs = [];
    obs.push(this.http.get<Record<string, string>>(`assets/i18n/json/en-GB.json`));
    if (region !== 'key-KEY') {
      obs.push(this.http.get<Record<string, string>>(`assets/i18n/json/${region}.json`));
    }
    return forkJoin(obs);
  }

  private getRemoteDictionaries(region: string): Observable<Record<string, string>[]> {
    return of([{}]);
  }
}
